import '../collection-action-menu/collection-action-menu.js';
import '../wishlist-collection-head/wishlist-collection-head.js';
import '../wishlist-image/wishlist-image.js';
import {CSSResult, html, LitElement, nothing, unsafeCSS} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {CollectionTrackingContext, Messages, Product, WishlistCollection} from '../../../@types/wishlist.js';
import {consume} from '@lit/context';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {repeat} from 'lit/directives/repeat.js';
import {sharedCss} from '../../../scripts/style-bundle/shared.js';
import styles from './wishlist-collection.scss';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';
import userIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_user.svg';
import {classMap} from 'lit/directives/class-map.js';

@customElement('ents-wishlist-collection')
export class UIWishlistCollection extends LitElement {
  @property({type: Object, attribute: 'wishlist-collection'})
  private wishlistCollection!: WishlistCollection;

  @property({type: Object, attribute: 'products'})
  private products!: Product[];

  @property({type: Boolean, attribute: 'curated-collection', reflect: true})
  private isCuratedCollection: boolean = false;

  @consume({context: i18nContext})
  private messages!: Messages;

  @consume({context: wishlistAppConfigContext})
  private wishlistAppConfig!: WishlistAppConfig;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  render() {
    const collectionTrackingContext: CollectionTrackingContext = {
      productsAmount: this.products.length,
      eventContext: 'collection_list',
    };
    const baseUrl = this.isCuratedCollection ?
      this.wishlistAppConfig.curatedCollectionEndpoint :
      this.wishlistAppConfig.collectionEndpoint;
    return html`
      <a
        class="ents-wishlist-list__link"
        href="${baseUrl}${this.wishlistCollection.id}/"
      >
        <div data-test-ents-wishlist-collection="${this.wishlistCollection.id}" class="ents-wishlist-list">
          <ents-wishlist-collection-head
            .wishlistCollection="${this.wishlistCollection}"
            .collectionTrackingContext="${collectionTrackingContext}"
            ?curated-collection="${this.isCuratedCollection}"
          >
          </ents-wishlist-collection-head>
          ${this.products.length > 0 ? this.renderProducts() : this.renderEmpty()}
          ${!this.isCuratedCollection && this.wishlistCollection.sharedWithTopCustomers ? html`
            <div
              class="bdk-badge-system ents-wishlist-list__top-customer-count ${classMap({'ents-wishlist-list__top-customer-count--empty': this.products.length === 0})}"
              variant="neutral">
              <span role="presentation"> ${hideIconFromAssistiveTechnology(userIcon())} </span>
              ${this.wishlistCollection.sharedWithTopCustomers.length}
            </div>` : nothing}
        </div>
      </a>
    `;
  }

  private renderProducts() {
    const showProducts = this.products.length > 5 ? this.products.slice(0, 4) : this.products.slice(0, 5);
    return html`
      <div class="ents-wishlist-list-preview-items">
        ${repeat(showProducts, p => p.syan, p => html`
          <ents-wishlist-image
            data-test-ents-wishlist-collection-picture="${p.syan}"
            image-url="${p.defaultImageUrls[0].url}"
            alt-text="${p.brandName} ${p.name}"
            ?is-beauty="${p.isBeauty}"
            brand-code="${p.brandCode}"
          ></ents-wishlist-image>
        `)}
        ${this.products.length > showProducts.length ? html`
          <div data-test-ents-wishlist-collection-more
               class="ents-wishlist-list-preview-items__more-hint ents-copy-bold"
          >
              +${this.products.length - showProducts.length}
          </div>
        ` : nothing}
      </div>
    `;
  }

  private renderEmpty() {
    return html`
      <div class="ents-copy ents-wishlist-list__empty-hint"
           data-test-ents-wishlist-collection-empty>
        <div class="ents-wishlist-list__empty-hint__link bdk-link-primary" size="mdium">
          ${this.messages['ents_wishlist_collection_details_link_text']}
        </div>
      </div>
    `;
  }
}
